import React, { memo, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';

import {
  Attachments,
  CareerLearning,
  CareerLesson,
  CareerVacancies,
  Course,
  Courses,
  Diplomas,
  Login,
  ErrorPage,
  Invoices,
  GrowthMedals,
  GrowthProfession,
  GrowthProfessionList,
  CareerSkillMap,
  Resume,
  SignUp,
  SurveyFill,
  Surveys,
} from 'pages';
import { MainTemplate, AttachmentsTemplate } from 'components/templates';
import { CareerApplications, CareerJourney, CareerLayout } from 'components/pages/Career';

import { useReactiveVar } from '@apollo/client';

import { appState, authState } from 'core/state';
import { ScrollToTop } from 'core/hooks/Routes/useScrollToTop';
import { PATHS } from './config';
import { SkillSlugRedirect } from './SkillSlugRedirect';
import { LEGACY_REDIRECTS } from './legacyRedirects';
import { LegacyRedirect } from './LegacyRedirect';
import { LmsAccessGuard } from './LmsAccessGuard';

export const AppRouter = memo(() => {
  const navigate = useNavigate();
  const location = useLocation();
  const { networkError } = useReactiveVar(appState);
  const { isLoggedIn } = useReactiveVar(authState);
  const currentState = appState();

  useEffect(() => {
    if (networkError) {
      navigate(`/${networkError}`);
      appState({
        ...currentState,
        networkError: null,
      });
    }
  }, [navigate, networkError, currentState]);

  useEffect(() => {
    if (!isLoggedIn && location.pathname !== PATHS.signUp) {
      navigate(PATHS.login);
    }
    if (isLoggedIn && location.pathname === PATHS.signUp) {
      navigate(PATHS.courses);
    }
  }, [isLoggedIn, navigate, location.pathname]);

  return (
    <ScrollToTop>
      {/* ТЗ 0074: приостановленному студенту — только /invoices. */}
      <LmsAccessGuard>
        <Routes>
          <Route path={PATHS.main} element={<Navigate to={PATHS.courses} />} />
          <Route
            path={PATHS.courses}
            element={
              <MainTemplate>
                <Courses />
              </MainTemplate>
            }
          />
          <Route
            path={PATHS.course}
            element={
              <MainTemplate>
                <Course />
              </MainTemplate>
            }
          />
          <Route
            path={PATHS.lesson}
            element={
              <MainTemplate>
                <Course />
              </MainTemplate>
            }
          />

          <Route
            path={PATHS.invoices}
            element={
              <MainTemplate>
                <Invoices />
              </MainTemplate>
            }
          />

          <Route
            path={PATHS.diplomas}
            element={
              <MainTemplate>
                <Diplomas />
              </MainTemplate>
            }
          />

          <Route
            path={PATHS.surveys}
            element={
              <MainTemplate>
                <Surveys />
              </MainTemplate>
            }
          />

          <Route
            path={PATHS.survey}
            element={
              <MainTemplate>
                <SurveyFill />
              </MainTemplate>
            }
          />

          {/*
          ═══ Карьерный маршрут (ТЗ 0069) ═══

          Один корень `/career` вместо трёх (`/career`, `/growth`, `/resume`),
          общая рамка `CareerLayout` — ролевой гард, плашка предпросмотра и
          под-навигация в ОДНОМ месте вместо восьми копий.

          Гейта членства на МАРШРУТЕ больше нет: не-участник остаётся на
          странице и читает объяснение с CTA. Раньше он получал здесь 404, и
          продающий раздел приходилось держать снаружи, на `/growth`.

          Порядок — от частного к общему: `/career/learning/:lessonId` раньше
          `/career/learning`, `/career/*` раньше `/career`. Ранжирование
          react-router 6 обеспечивает это и само, порядок здесь — для читающего.
        */}
          <Route
            path={PATHS.careerLesson}
            element={
              <MainTemplate>
                <CareerLayout>
                  <CareerLesson />
                </CareerLayout>
              </MainTemplate>
            }
          />

          <Route
            path={PATHS.careerLearning}
            element={
              <MainTemplate>
                <CareerLayout>
                  <CareerLearning />
                </CareerLayout>
              </MainTemplate>
            }
          />

          <Route
            path={PATHS.careerProfile}
            element={
              <MainTemplate>
                <CareerLayout>
                  <Resume />
                </CareerLayout>
              </MainTemplate>
            }
          />

          {/*
          Адрес одного навыка стал СОСТОЯНИЕМ карты, а не отдельной страницей
          (ТЗ 0070): выбор узла иначе означал бы переход, а «назад» — уход с
          карты вместо снятия выбора. Старые ссылки живут редиректом.

          🔴 Без `MainTemplate`, как и прочие редиректы: шаблон монтирует
          шапку, та заказывает `myCareerCenter`, и всё это размонтируется в том
          же кадре — пустая шапка мигает, а в сеть уходит лишний запрос.
        */}
          <Route path={PATHS.careerSkill} element={<SkillSlugRedirect />} />

          <Route
            path={PATHS.careerSkills}
            element={
              <MainTemplate>
                <CareerLayout>
                  <CareerSkillMap />
                </CareerLayout>
              </MainTemplate>
            }
          />

          <Route
            path={PATHS.careerProfession}
            element={
              <MainTemplate>
                <CareerLayout>
                  <GrowthProfession />
                </CareerLayout>
              </MainTemplate>
            }
          />

          <Route
            path={PATHS.careerProfessions}
            element={
              <MainTemplate>
                <CareerLayout>
                  <GrowthProfessionList />
                </CareerLayout>
              </MainTemplate>
            }
          />

          <Route
            path={PATHS.careerJobs}
            element={
              <MainTemplate>
                <CareerLayout>
                  <CareerVacancies />
                </CareerLayout>
              </MainTemplate>
            }
          />

          <Route
            path={PATHS.careerApplications}
            element={
              <MainTemplate>
                <CareerLayout>
                  <CareerApplications />
                </CareerLayout>
              </MainTemplate>
            }
          />

          <Route
            path={PATHS.careerAchievements}
            element={
              <MainTemplate>
                <CareerLayout>
                  <GrowthMedals />
                </CareerLayout>
              </MainTemplate>
            }
          />

          <Route
            path={PATHS.careerCenter}
            element={
              <MainTemplate>
                <CareerLayout>
                  <CareerJourney />
                </CareerLayout>
              </MainTemplate>
            }
          />

          {/*
          Переезд со старых адресов. Блок стоит ПОСЛЕ живых `/career/*` и ДО
          `path="*"`.

          🔴 Без `MainTemplate`: шаблон монтирует шапку, та заказывает
          `myCareerCenter`, и всё это размонтируется в том же кадре — пустая
          шапка мигает, а в сеть уходит лишний запрос на каждый переход по
          старой ссылке.
        */}
          {LEGACY_REDIRECTS.map(({ from, to }) => (
            <Route key={from} path={from} element={<LegacyRedirect to={to} />} />
          ))}

          <Route
            path={PATHS.attachment}
            element={
              <AttachmentsTemplate>
                <Attachments />
              </AttachmentsTemplate>
            }
          />

          <Route path={PATHS.signUp} key={PATHS.signUp} element={<SignUp />} />
          <Route path={PATHS.login} key={PATHS.login} element={<Login />} />
          <Route path="*" element={<ErrorPage status={404} />} />
          <Route path="/404" element={<ErrorPage status={404} />} />
          <Route path="/500" element={<ErrorPage status={500} />} />
          <Route path="/503" element={<ErrorPage status={503} />} />
          <Route path="/error" element={<ErrorPage status="error" />} />
        </Routes>
      </LmsAccessGuard>
    </ScrollToTop>
  );
});
